import {
  createContext,
  Dispatch,
  ReactNode,
  SetStateAction,
  useCallback,
  useContext,
  useEffect,
  useState,
} from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "react-toastify";
import { ProductivityDashboardService } from "../../services/Dashboards/ProductivityDashboardService";

interface DashboardFilters {
  collaborator_id?: number | null;
  period?: string | null;
  start_date?: string | null;
  end_date?: string | null;
  category_id?: number | null;
}

interface ContextProps {
  filters: DashboardFilters;
  setFilters: Dispatch<SetStateAction<DashboardFilters>>;

  kpis: any | null;
  sessions: any | null;
  materials: any | null;
  ranking: any | null;
  peakHours: any | null;
  qualityCorrections: any | null;
  qualityRanking: any | null;
  collaborators: any[];
  categories: any[];

  isLoadingKpis: boolean;
  isLoadingSessions: boolean;
  isLoadingMaterials: boolean;
  isLoadingRanking: boolean;
  isLoadingPeakHours: boolean;
  isLoadingQualityCorrections: boolean;
  isLoadingQualityRanking: boolean;
  isLoading: boolean;

  error: string;
  setError: Dispatch<SetStateAction<string>>;

  fetchAllData: () => Promise<void>;
}

export const ProductivityDashboardContext = createContext<ContextProps>(
  {} as ContextProps
);

interface ProductivityDashboardProviderProps {
  children: ReactNode;
}

export const ProductivityDashboardProvider = ({
  children,
}: ProductivityDashboardProviderProps) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [filters, setFilters] = useState<DashboardFilters>({});

  const [kpis, setKpis] = useState<any | null>(null);
  const [sessions, setSessions] = useState<any | null>(null);
  const [materials, setMaterials] = useState<any | null>(null);
  const [ranking, setRanking] = useState<any | null>(null);
  const [peakHours, setPeakHours] = useState<any | null>(null);
  const [qualityCorrections, setQualityCorrections] = useState<any | null>(null);
  const [qualityRanking, setQualityRanking] = useState<any | null>(null);
  const [collaborators, setCollaborators] = useState<any[]>([]);
  const [categories, setCategories] = useState<any[]>([]);

  const [isLoadingKpis, setIsLoadingKpis] = useState(false);
  const [isLoadingSessions, setIsLoadingSessions] = useState(false);
  const [isLoadingMaterials, setIsLoadingMaterials] = useState(false);
  const [isLoadingRanking, setIsLoadingRanking] = useState(false);
  const [isLoadingPeakHours, setIsLoadingPeakHours] = useState(false);
  const [isLoadingQualityCorrections, setIsLoadingQualityCorrections] = useState(false);
  const [isLoadingQualityRanking, setIsLoadingQualityRanking] = useState(false);

  const [error, setError] = useState<string>("");

  const service = new ProductivityDashboardService();

  const isLoading =
    isLoadingKpis ||
    isLoadingSessions ||
    isLoadingMaterials ||
    isLoadingRanking ||
    isLoadingPeakHours ||
    isLoadingQualityCorrections ||
    isLoadingQualityRanking;

  // Sincronizar filtros com URL params
  useEffect(() => {
    const collaborator_id = searchParams.get("collaborator_id");
    const period = searchParams.get("period");
    const start_date = searchParams.get("start_date");
    const end_date = searchParams.get("end_date");
    const category_id = searchParams.get("category_id");

    setFilters({
      collaborator_id: collaborator_id ? Number(collaborator_id) : null,
      period,
      start_date,
      end_date,
      category_id: category_id ? Number(category_id) : null,
    });
  }, [searchParams]);

  // Atualizar URL quando filtros mudam
  const updateUrlParams = useCallback((newFilters: DashboardFilters) => {
    const params = new URLSearchParams();

    if (newFilters.collaborator_id)
      params.append("collaborator_id", String(newFilters.collaborator_id));
    if (newFilters.period) params.append("period", newFilters.period);
    if (newFilters.start_date) params.append("start_date", newFilters.start_date);
    if (newFilters.end_date) params.append("end_date", newFilters.end_date);
    if (newFilters.category_id)
      params.append("category_id", String(newFilters.category_id));

    setSearchParams(params);
  }, [setSearchParams]);

  // Buscar todos os dados em paralelo
  const fetchAllData = useCallback(async () => {
    try {
      setError("");

      // Remover valores nulos dos filtros
      const cleanFilters = Object.fromEntries(
        Object.entries(filters).filter(([_, value]) => value !== null && value !== undefined && value !== "")
      );

      setIsLoadingKpis(true);
      setIsLoadingSessions(true);
      setIsLoadingMaterials(true);
      setIsLoadingRanking(true);
      setIsLoadingPeakHours(true);
      setIsLoadingQualityCorrections(true);
      setIsLoadingQualityRanking(true);

      const [kpisRes, kpisErr] = await service.getKpis(cleanFilters);
      const [sessionsRes, sessionsErr] = await service.getSessions(cleanFilters);
      const [materialsRes, materialsErr] = await service.getMaterials(cleanFilters);
      const [rankingRes, rankingErr] = await service.getRanking(cleanFilters);
      const [peakHoursRes, peakHoursErr] = await service.getPeakHours(cleanFilters);
      const [qualityCorrectionsRes, qualityCorrectionsErr] = await service.getQualityCorrections(cleanFilters);
      const [qualityRankingRes, qualityRankingErr] = await service.getQualityRanking(cleanFilters);

      setIsLoadingKpis(false);
      setIsLoadingSessions(false);
      setIsLoadingMaterials(false);
      setIsLoadingRanking(false);
      setIsLoadingPeakHours(false);
      setIsLoadingQualityCorrections(false);
      setIsLoadingQualityRanking(false);

      if (kpisErr) {
        setError(kpisRes?.message || kpisErr);
        setKpis(null);
      } else {
        setKpis(kpisRes?.data);
      }

      if (sessionsErr) {
        setError(sessionsRes?.message || sessionsErr);
        setSessions(null);
      } else {
        setSessions(sessionsRes?.data);
      }

      if (materialsErr) {
        setError(materialsRes?.message || materialsErr);
        setMaterials(null);
      } else {
        setMaterials(materialsRes?.data);
      }

      if (rankingErr) {
        setError(rankingRes?.message || rankingErr);
        setRanking(null);
      } else {
        setRanking(rankingRes?.data);
      }

      if (peakHoursErr) {
        setError(peakHoursRes?.message || peakHoursErr);
        setPeakHours(null);
      } else {
        setPeakHours(peakHoursRes?.data);
      }

      if (qualityCorrectionsErr) {
        setError(qualityCorrectionsRes?.message || qualityCorrectionsErr);
        setQualityCorrections(null);
      } else {
        setQualityCorrections(qualityCorrectionsRes?.data);
      }

      if (qualityRankingErr) {
        setError(qualityRankingRes?.message || qualityRankingErr);
        setQualityRanking(null);
      } else {
        setQualityRanking(qualityRankingRes?.data);
      }
    } catch (e: any) {
      setIsLoadingKpis(false);
      setIsLoadingSessions(false);
      setIsLoadingMaterials(false);
      setIsLoadingRanking(false);
      setIsLoadingPeakHours(false);
      setIsLoadingQualityCorrections(false);
      setIsLoadingQualityRanking(false);
      setError("Erro ao carregar dados do dashboard");
    }
  }, [filters]);


  // Extrair colaboradores dos dados de sessions
  useEffect(() => {
    if (sessions?.collaborators && sessions.collaborators.length > 0) {
      const colabs = sessions.collaborators.map((c: any) => ({
        id: c.collaborator_id,
        name: c.collaborator_name,
      }));
      setCollaborators(colabs);
    }
  }, [sessions]);

  // Buscar categorias
  useEffect(() => {
    const fetchCategories = async () => {
      const categoryParams = Object.fromEntries(
        Object.entries({
          collaborator_id: filters.collaborator_id,
          period: filters.period,
          start_date: filters.start_date,
          end_date: filters.end_date,
        }).filter(([_, value]) => value !== null && value !== undefined && value !== "")
      );
      const [categoriesRes, categoriesErr] = await service.getCategories(categoryParams);
      if (!categoriesErr && categoriesRes?.data?.data) {
        setCategories(categoriesRes.data.data);
      }
    };
    fetchCategories();
  }, [filters.collaborator_id, filters.period, filters.start_date, filters.end_date]);

  // Buscar dados ao montar ou quando filtros mudam
  useEffect(() => {
    fetchAllData();
  }, [fetchAllData]);

  // Mostrar erro em toast
  useEffect(() => {
    if (error) {
      toast.error(error);
    }
  }, [error]);

  // Sobrescrever setFilters para atualizar URL também
  const setFiltersWithUrl = useCallback((newFilters: DashboardFilters | ((prev: DashboardFilters) => DashboardFilters)) => {
    if (typeof newFilters === "function") {
      setFilters((prev) => {
        const updated = newFilters(prev);
        updateUrlParams(updated);
        return updated;
      });
    } else {
      setFilters(newFilters);
      updateUrlParams(newFilters);
    }
  }, [updateUrlParams]);

  return (
    <ProductivityDashboardContext.Provider
      value={{
        filters,
        setFilters: setFiltersWithUrl,
        kpis,
        sessions,
        materials,
        ranking,
        peakHours,
        qualityCorrections,
        qualityRanking,
        collaborators,
        categories,
        isLoadingKpis,
        isLoadingSessions,
        isLoadingMaterials,
        isLoadingRanking,
        isLoadingPeakHours,
        isLoadingQualityCorrections,
        isLoadingQualityRanking,
        isLoading,
        error,
        setError,
        fetchAllData,
      }}
    >
      {children}
    </ProductivityDashboardContext.Provider>
  );
};

export const useProductivityDashboard = () =>
  useContext(ProductivityDashboardContext);
